import React, { useState, useEffect } from "react";
import { Plus, Trash2, Info } from "lucide-react";
import api from "../api";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function WeeklyScheduleEditor({ schedule, onChange }) {
    const [bookingSettings, setBookingSettings] = useState(null);
    const [branches, setBranches] = useState([]);

    useEffect(() => {
        let isMounted = true;
        api.getGeneralSettings()
            .then(res => {
                if (isMounted && res.data) {
                    setBookingSettings({
                        allow_unpaid_booking: res.data.allow_unpaid_booking ?? true,
                        unpaid_fee_difference_type: res.data.unpaid_fee_difference_type || 'flat',
                        unpaid_fee_difference_value: parseFloat(res.data.unpaid_fee_difference_value) || 0
                    });
                }
            })
            .catch(err => {
                console.error("Failed to load settings in WeeklyScheduleEditor", err);
            });
        // include_inactive so a shift that already points at an archived
        // branch still shows its name instead of going blank.
        api.fetchOrgBranches(true)
            .then(res => {
                if (isMounted && res.data) setBranches(res.data);
            })
            .catch(err => {
                console.error("Failed to load branches in WeeklyScheduleEditor", err);
            });
        return () => { isMounted = false; };
    }, []);

    const defaultBranchId = branches.find(b => b.is_default)?.id ?? null;

    const getUnpaidFee = (baseFee) => {
        if (!bookingSettings || !bookingSettings.allow_unpaid_booking) return null;
        const feeNum = parseFloat(baseFee) || 0;
        const diffVal = bookingSettings.unpaid_fee_difference_value;
        if (!diffVal) return feeNum;
        if (bookingSettings.unpaid_fee_difference_type === 'percent') {
            return Math.round(feeNum + (feeNum * diffVal / 100));
        }
        return Math.round(feeNum + diffVal);
    };

    const handleSlotDurationChange = (e) => {
        onChange({ ...schedule, slot_duration: parseInt(e.target.value) || 30 });
    };

    const toggleDay = (day) => {
        const newSchedule = { ...schedule };
        if (!newSchedule[day]) newSchedule[day] = { is_working: false, shifts: [] };
        
        newSchedule[day].is_working = !newSchedule[day].is_working;
        if (newSchedule[day].is_working && newSchedule[day].shifts.length === 0) {
            newSchedule[day].shifts = [{ start: "09:00", end: "17:00", fee: 1500, branch_id: defaultBranchId }];
        }
        onChange(newSchedule);
    };

    const addShift = (day) => {
        const newSchedule = { ...schedule };
        newSchedule[day].shifts.push({ start: "09:00", end: "17:00", fee: 1500, branch_id: defaultBranchId });
        onChange(newSchedule);
    };

    const updateShift = (day, index, field, value) => {
        const newSchedule = { ...schedule };
        let parsedValue = value;
        if (field === 'fee') parsedValue = parseInt(value) || 0;
        else if (field === 'branch_id') parsedValue = value === "" ? null : parseInt(value);
        newSchedule[day].shifts[index][field] = parsedValue;
        onChange(newSchedule);
    };

    const removeShift = (day, index) => {
        const newSchedule = { ...schedule };
        newSchedule[day].shifts.splice(index, 1);
        if (newSchedule[day].shifts.length === 0) {
            newSchedule[day].is_working = false;
        }
        onChange(newSchedule);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-base font-bold text-gray-900">Weekly Schedule & Rates</h3>
                    <p className="text-xs text-gray-500 mt-1">Configure working hours and consultation fees for each day.</p>
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm shrink-0">
                    <label className="text-xs font-semibold text-gray-600">Slot Duration:</label>
                    <select 
                        value={schedule?.slot_duration || 30} 
                        onChange={handleSlotDurationChange}
                        className="text-sm font-bold text-blue-600 bg-transparent border-none focus:ring-0 cursor-pointer outline-none"
                    >
                        <option value={5}>5 mins</option>
                        <option value={10}>10 mins</option>
                        <option value={15}>15 mins</option>
                        <option value={30}>30 mins</option>
                        <option value={45}>45 mins</option>
                        <option value={60}>60 mins</option>
                    </select>
                    <span className="text-gray-300">|</span>
                    <label htmlFor="buffer_minutes" className="normal-case font-sans tracking-normal text-xs font-semibold text-gray-600" title="Free time kept after each session before the next one can be booked">Gap after each:</label>
                    <select id="buffer_minutes"
                        value={schedule?.buffer_minutes || 0}
                        onChange={(e) => onChange({ ...schedule, buffer_minutes: parseInt(e.target.value, 10) || 0 })}
                        className="text-sm font-bold text-blue-600 bg-transparent border-none focus:ring-0 cursor-pointer outline-none"
                    >
                        <option value={0}>none</option>
                        <option value={5}>5 mins</option>
                        <option value={10}>10 mins</option>
                        <option value={15}>15 mins</option>
                        <option value={30}>30 mins</option>
                    </select>
                </div>
            </div>

            {bookingSettings?.allow_unpaid_booking && bookingSettings?.unpaid_fee_difference_value !== 0 && (
                <div className="mx-5 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2 text-xs text-blue-800">
                    <Info size={16} className="text-blue-600 shrink-0" />
                    <span>
                        <strong>Organization Policy:</strong> Pay at Clinic bookings have a{" "}
                        <strong>
                            {bookingSettings.unpaid_fee_difference_type === "percent"
                                ? `${bookingSettings.unpaid_fee_difference_value}%`
                                : `₹${bookingSettings.unpaid_fee_difference_value}`}
                        </strong>{" "}
                        fee adjustment over base prepaid fee. Calculated Pay at Clinic price is previewed alongside each fee.
                    </span>
                </div>
            )}

            <div className="p-5 space-y-4">
                {DAYS.map(day => {
                    const dayConfig = schedule?.[day] || { is_working: false, shifts: [] };
                    return (
                        <div key={day} className={`rounded-xl border transition-all duration-200 ${dayConfig.is_working ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100 bg-gray-50/50'}`}>
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4 p-4">
                                <div className="w-full sm:w-36 flex items-center justify-between sm:justify-start gap-3 pt-1 shrink-0">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <div className="relative flex items-center justify-center">
                                            <input 
                                                type="checkbox" 
                                                checked={dayConfig.is_working}
                                                onChange={() => toggleDay(day)}
                                                className="peer sr-only"
                                            />
                                            <div className={`w-10 h-6 rounded-full transition-colors ${dayConfig.is_working ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${dayConfig.is_working ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                        </div>
                                        <span className={`text-sm font-semibold capitalize ${dayConfig.is_working ? 'text-gray-900' : 'text-gray-500'}`}>{day}</span>
                                    </label>
                                </div>
                                
                                <div className="flex-1 space-y-3">
                                    {!dayConfig.is_working && (
                                        <div className="text-sm text-gray-400 italic pt-1">Not working on this day</div>
                                    )}
                                    {dayConfig.is_working && dayConfig.shifts.map((shift, idx) => (
                                        <div key={idx} className="flex flex-wrap items-center gap-5 bg-white p-4 rounded-xl border border-secondary-200 shadow-sm group hover:border-primary-300 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <input 
                                                    type="time" 
                                                    value={shift.start} 
                                                    onChange={e => updateShift(day, idx, 'start', e.target.value)}
                                                    onWheel={e => e.target.blur()}
                                                    className="w-32 border border-secondary-200 rounded-lg px-3 py-2 text-sm font-medium text-secondary-900 bg-secondary-50 focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                                />
                                                <span className="text-xs font-bold text-secondary-400 px-1">TO</span>
                                                <input 
                                                    type="time" 
                                                    value={shift.end} 
                                                    onChange={e => updateShift(day, idx, 'end', e.target.value)}
                                                    onWheel={e => e.target.blur()}
                                                    className="w-32 border border-secondary-200 rounded-lg px-3 py-2 text-sm font-medium text-secondary-900 bg-secondary-50 focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                                />
                                            </div>
                                            
                                            <div className="h-6 w-px bg-secondary-200 hidden sm:block mx-2"></div>

                                            {branches.length > 0 && (
                                                <select
                                                    value={shift.branch_id ?? ""}
                                                    onChange={e => updateShift(day, idx, 'branch_id', e.target.value)}
                                                    className="w-40 border border-secondary-200 rounded-lg px-2.5 py-2 text-sm font-medium text-secondary-900 bg-secondary-50 focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                                    title="Branch this shift is held at"
                                                >
                                                    <option value="">Org Default Address</option>
                                                    {branches.map(b => (
                                                        <option key={b.id} value={b.id}>
                                                            {b.name}{!b.is_active ? " (archived)" : ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}

                                            <div className="h-6 w-px bg-secondary-200 hidden sm:block mx-2"></div>

                                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 sm:flex-none">
                                                <div className="flex items-center">
                                                    <span className="text-sm font-medium text-secondary-500 bg-secondary-50 border border-r-0 border-secondary-200 rounded-l-lg px-3 py-2">₹</span>
                                                    <input 
                                                        type="number" 
                                                        value={shift.fee} 
                                                        onChange={e => updateShift(day, idx, 'fee', e.target.value)}
                                                        onWheel={e => e.target.blur()}
                                                        className="w-full sm:w-28 border border-secondary-200 rounded-r-lg px-3 py-2 text-sm font-bold text-secondary-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-all"
                                                        placeholder="Prepaid Fee"
                                                        title="Base Prepaid Fee"
                                                    />
                                                </div>
                                                {bookingSettings?.allow_unpaid_booking && bookingSettings.unpaid_fee_difference_value !== 0 && (
                                                    <span 
                                                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 whitespace-nowrap shadow-xs" 
                                                        title="Calculated price when booked without payment (Pay at Clinic)"
                                                    >
                                                        Pay at Clinic: ₹{getUnpaidFee(shift.fee)}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <button 
                                                type="button" 
                                                onClick={() => removeShift(day, idx)}
                                                className="ml-auto bg-white border border-secondary-200 text-secondary-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 p-2 rounded-lg shadow-sm transition-colors sm:opacity-50 sm:group-hover:opacity-100"
                                                title="Remove Shift"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                    {dayConfig.is_working && (
                                        <button 
                                            type="button"
                                            onClick={() => addShift(day)}
                                            className="text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors border border-blue-100"
                                        >
                                            <Plus size={16} /> Add another shift
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
